import React, { useCallback, useEffect, useRef, useState, createContext, useContext } from 'react';
import { Sheet, Scroll } from '@silk-hq/components';

import { fullHeight, scale } from '@components/frame';

type LongSheetContextType = {
  setTrack: (track: 'top' | 'bottom') => void;
  restingOutside: boolean;
};

const LongSheetContext = createContext<LongSheetContextType | null>(null);

type SheetRootProps = React.ComponentPropsWithoutRef<typeof Sheet.Root>;
type LongSheetRootProps = Omit<SheetRootProps, 'license'> & {
  license?: SheetRootProps['license'];
};

const LongSheetRoot = React.forwardRef<React.ElementRef<typeof Sheet.Root>, LongSheetRootProps>(({ children, ...restProps }, ref) => {
  return (
    <Sheet.Root license="commercial" {...restProps} ref={ref}>
      {children}
    </Sheet.Root>
  );
});
LongSheetRoot.displayName = 'LongSheet.Root';

const LongSheetView = React.forwardRef<React.ElementRef<typeof Sheet.View>, React.ComponentPropsWithoutRef<typeof Sheet.View>>(
  ({ children, onTravelStatusChange, ...restProps }, ref) => {
    const [restingOutside, setRestingOutside] = useState(false);
    const [track, setTrack] = useState<'top' | 'bottom'>('bottom');

    useEffect(() => {
      if (restingOutside) {
        setTrack('bottom');
      }
    }, [restingOutside]);

    return (
      <LongSheetContext.Provider value={{ setTrack, restingOutside }}>
        <Sheet.View
          contentPlacement="center"
          tracks={track}
          swipeDismissal={false}
          swipeOvershoot={false}
          nativeEdgeSwipePrevention={true}
          enteringAnimationSettings={{
            easing: 'spring',
            stiffness: 480,
            damping: 45,
            mass: 1.5,
          }}
          onTravelStatusChange={(status) => {
            setRestingOutside(status === 'idleOutside');
            onTravelStatusChange?.(status);
          }}
          {...restProps}
          ref={ref}
        >
          {children}
        </Sheet.View>
      </LongSheetContext.Provider>
    );
  }
);
LongSheetView.displayName = 'LongSheet.View';

const LongSheetBackdrop = React.forwardRef<React.ElementRef<typeof Sheet.Backdrop>, React.ComponentPropsWithoutRef<typeof Sheet.Backdrop>>(
  ({ ...restProps }, ref) => {
    return <Sheet.Backdrop themeColorDimming="auto" {...restProps} ref={ref} />;
  }
);
LongSheetBackdrop.displayName = 'LongSheet.Backdrop';

type LongSheetContentProps = React.ComponentPropsWithoutRef<typeof Sheet.Content> & {
  handleScroll?: boolean; // Default to true for backward compatibility
};

const LongSheetContent = React.forwardRef<React.ElementRef<typeof Sheet.Content>, LongSheetContentProps>(
  ({ children, handleScroll = true, ...restProps }, ref) => {
    const scrollRef = useRef<any>(null);
    const context = useContext(LongSheetContext);
    if (!context) {
      throw new Error('LongSheetContent must be used within a LongSheetContext.Provider');
    }
    const { setTrack, restingOutside } = context;

    const scrollHandler = useCallback(
      ({ progress }: any) => {
        if (restingOutside) return;
        setTrack(progress < 0.5 ? 'bottom' : 'top');
      },
      [restingOutside, setTrack]
    );

    // If handleScroll is false, render children directly without scroll wrapper
    if (!handleScroll) {
      return (
        <Sheet.Content style={{ height: '100%', backgroundColor: 'transparent' }} {...restProps} ref={ref}>
          {children}
        </Sheet.Content>
      );
    }

    // Default behavior with scroll handling
    return (
      <Sheet.Content asChild style={{ height: '100%', backgroundColor: 'transparent' }} {...restProps} ref={ref}>
        <Scroll.Root componentRef={scrollRef} asChild>
          <Scroll.View onScroll={scrollHandler}>
            <Scroll.Content>{children}</Scroll.Content>
          </Scroll.View>
        </Scroll.Root>
      </Sheet.Content>
    );
  }
);
LongSheetContent.displayName = 'LongSheet.Content';

const LongSheetPortal = Sheet.Portal;
const LongSheetTrigger = Sheet.Trigger;
const LongSheetHandle = Sheet.Handle;
const LongSheetOutlet = Sheet.Outlet;
const LongSheetTitle = Sheet.Title;
const LongSheetDescription = Sheet.Description;

export default {
  Root: LongSheetRoot,
  Portal: LongSheetPortal,
  View: LongSheetView,
  Backdrop: LongSheetBackdrop,
  Content: LongSheetContent,
  Trigger: LongSheetTrigger,
  Handle: LongSheetHandle,
  Outlet: LongSheetOutlet,
  Title: LongSheetTitle,
  Description: LongSheetDescription,
};
